'use client';

import { useEffect, useState, type ReactNode } from 'react';

import { Button, FormField, InputForm } from '@/components/ui';
import { toast, useForm, z, zodResolver } from '@/libs';
import { parseDigits } from '@/utils';

export type OtpVerifyResult = 'success' | 'invalid' | 'blocked' | 'error';
export type OtpDescription = ReactNode | ((maskedPhone: string | undefined) => ReactNode);

export type OtpVerificationModalProps = {
  maskedPhone?: string;
  title?: string;
  description?: OtpDescription;
  onVerify: (code: string) => Promise<OtpVerifyResult>;
  onResend: () => Promise<string | undefined>;
  onClose: () => void;
  onSuccess?: () => void;
};

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

const formatCooldown = (totalSeconds: number) => {
  const safeSeconds = Math.max(totalSeconds, 0);
  const minutes = Math.floor(safeSeconds / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (safeSeconds % 60).toString().padStart(2, '0');

  return `${minutes}:${seconds}`;
};

const otpSchema = z.object({
  code: z.string().length(OTP_LENGTH, `Digite os ${OTP_LENGTH} dígitos do código.`),
});

type OtpFormData = z.infer<typeof otpSchema>;

export const OtpVerificationModal = ({
  maskedPhone,
  title = 'Verificação por SMS',
  description,
  onVerify,
  onResend,
  onClose,
  onSuccess,
}: OtpVerificationModalProps) => {
  const [displayedPhone, setDisplayedPhone] = useState(maskedPhone);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<OtpFormData>({
    resolver: zodResolver(otpSchema),
    mode: 'onChange',
    defaultValues: { code: '' },
  });

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const onSubmit = handleSubmit(async ({ code }) => {
    setSubmitting(true);
    setErrorMessage(null);

    try {
      const result = await onVerify(code);

      if (result === 'success') {
        onClose();
        onSuccess?.();
        return;
      }

      if (result === 'invalid') {
        setErrorMessage('Código inválido ou expirado. Verifique e tente novamente.');
        reset({ code: '' });
      } else if (result === 'blocked') {
        setBlocked(true);
        setErrorMessage('Muitas tentativas. Solicite um novo código para continuar.');
        reset({ code: '' });
      } else {
        setErrorMessage('Não foi possível validar o código. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  });

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;

    setResending(true);
    setErrorMessage(null);

    try {
      const newMaskedPhone = await onResend();
      if (newMaskedPhone) setDisplayedPhone(newMaskedPhone);
      setBlocked(false);
      reset({ code: '' });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      toast.success('Enviamos um novo código para o seu telefone.');
    } finally {
      setResending(false);
    }
  };

  const defaultDescription = displayedPhone ? (
    <>
      Enviamos um código de {OTP_LENGTH} dígitos para o telefone{' '}
      <span className="font-semibold text-foreground">{displayedPhone}</span>.
    </>
  ) : (
    `Enviamos um código de ${OTP_LENGTH} dígitos para o seu telefone cadastrado.`
  );

  const renderedDescription =
    typeof description === 'function' ? description(displayedPhone) : description;

  return (
    <div
      data-cy="otp-verification-modal"
      className="flex w-full flex-col gap-4 rounded-2xl bg-background p-6 text-foreground"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex flex-col gap-1 text-center">
        <h2 className="text-lg font-bold">{title}</h2>
        <p className="text-sm text-muted-foreground">
          {renderedDescription ?? defaultDescription}
        </p>
        <span className="mx-auto mt-1 w-fit rounded-md border border-foreground/10 bg-foreground/5 px-2 py-1 text-xs font-medium text-muted-foreground">
          Enviado por SMS
        </span>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <FormField error={errors.code}>
          <InputForm
            data-cy="otp-verification-modal-code-input"
            control={control}
            name="code"
            formatFunction={(text) => parseDigits(text).slice(0, OTP_LENGTH)}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={OTP_LENGTH}
            placeholder="000000"
            disabled={submitting || blocked}
            className="text-center text-lg tracking-[0.5em]"
            aria-label="Código de verificação"
          />
        </FormField>

        {errorMessage && (
          <p className="text-center text-sm text-error" role="alert">
            {errorMessage}
          </p>
        )}

        <Button
          data-cy="otp-verification-modal-submit-button"
          type="submit"
          variant="secondary"
          className="w-full"
          loading={submitting}
          loadingText="Verificando"
          disabled={!isValid || blocked}
        >
          Verificar
        </Button>

        <Button
          data-cy="otp-verification-modal-resend-button"
          type="button"
          variant="link"
          className="mx-auto"
          onClick={handleResend}
          loading={resending}
          loadingText="Reenviando"
          disabled={resendCooldown > 0}
        >
          {resendCooldown > 0
            ? `Reenviar código em ${formatCooldown(resendCooldown)}`
            : 'Reenviar código'}
        </Button>
      </form>
    </div>
  );
};
